import * as React from 'react';
import moment from 'moment';
import {
	ToolOutlined,
	DollarOutlined,
	ProfileOutlined,
	NodeIndexOutlined,
} from '@ant-design/icons';
import DeliverableStatusDisplay from '../deliverable_status_display/DeliverableStatusDisplay';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	formatCurrency,
	workOrderMetricsTotal,
	workOrdersDoneCount,
} from '../../utils/DeliverableUtils';
import { cn } from '../../utils/ClassNamesUtils';

// Deliverables carry several assignees (assignedTo is a list of emails), and the row
// only has the width for a name when there is exactly one. Beyond that it becomes
// overlapping avatars — hover any of them for the name — with a +N once the list runs
// past three, matching how the app shows a group of people elsewhere (UserNameList).
const MAX_VISIBLE_ASSIGNEES = 3;

export const AssignedToDisplay = ({ emails }) => {
	if (!emails.length) {
		return null;
	}
	if (emails.length === 1) {
		return (
			<span className="footnoteregular text-character-secondary">
				<UserNameWithEmail email={emails[0]} mode="inline" />
			</span>
		);
	}
	const visible = emails.slice(0, MAX_VISIBLE_ASSIGNEES);
	const overflow = emails.length - visible.length;
	return (
		<span className="flex items-center">
			{visible.map((email) => (
				// Negative margin overlaps the avatars the way antd's Avatar.Group does;
				// Avatar.Group itself only styles direct Avatar children, and each of these
				// is a UserName wrapper that resolves the email to a contact.
				<span key={email} className="-mr-1.5 last:mr-0">
					<UserNameWithEmail email={email} mode="avatar" placement="top" size="small" />
				</span>
			))}
			{overflow ? (
				<span className="footnoteregular ml-2 text-character-secondary">+{overflow}</span>
			) : null}
		</span>
	);
};

const DeliverableListItem = ({ deliverable, currency, dependsOnName, selected, onClick }) => {
	const fmt = (amount) => (currency ? currency.symbolFormat(amount, 0) : formatCurrency(amount));
	const status = nullSafeGet('status', deliverable);
	const workOrderMetrics = nullSafeGet('workOrderMetrics', deliverable);
	const taskMetrics = nullSafeGet('taskMetrics', deliverable);
	const budget = nullSafeGet('budget', deliverable);
	const budgetUsed = nullSafeGet('budgetUsed', deliverable);
	const dueDate = nullSafeGet('dueDate', deliverable);
	const assignedTo = nullSafeGetOrElse('assignedTo', deliverable, []);
	const overBudget = budget != null && budgetUsed != null && budgetUsed > budget;

	return (
		<div
			onClick={onClick}
			className={`cursor-pointer border-0 border-l-2 border-solid px-6 py-3 ${
				selected ? 'border-owgray bg-owgray' : 'border-transparent hover:bg-grey-2'
			}`}
		>
			<div className="mb-2 flex items-center justify-between">
				<div className="flex items-center gap-2">
					<DeliverableStatusDisplay status={status} />
					{dueDate ? (
						<span className="footnoteregular text-character-secondary">
							Due: {moment(dueDate).format('M/D')}
						</span>
					) : null}
				</div>
				<AssignedToDisplay emails={assignedTo} />
			</div>
			<div className="bodyregular mb-2 text-character-primary">
				{nullSafeGet('name', deliverable)}
			</div>
			{dependsOnName ? (
				<div className="footnoteregular mb-2 flex items-center gap-1 text-character-secondary">
					<NodeIndexOutlined className="text-[12px]" />
					<span className="truncate">After {dependsOnName}</span>
				</div>
			) : null}
			<div className="footnoteregular flex flex-wrap items-center gap-x-4 text-character-secondary">
				{workOrderMetrics ? (
					<span className="flex items-center gap-1">
						<ToolOutlined className="text-[12px]" />
						<span className="footnoteMedium text-character-primary">
							{workOrdersDoneCount(workOrderMetrics)}/{workOrderMetricsTotal(workOrderMetrics)}
						</span>
					</span>
				) : null}
				{budget != null ? (
					<span className="flex items-center gap-1">
						<DollarOutlined className="text-[12px]" />
						<span
							className={cn('footnoteMedium text-character-primary', {
								'text-sunsetOrange-8': overBudget,
							})}
						>
							{fmt(budgetUsed != null ? budgetUsed : 0)} / {fmt(budget)}
						</span>
					</span>
				) : null}
				{taskMetrics ? (
					<span className="flex items-center gap-1">
						<ProfileOutlined className="text-[12px]" />
						<span className="footnoteMedium text-character-primary">
							{nullSafeGetOrElse('tasksCompleted', taskMetrics, 0)}/
							{nullSafeGetOrElse('tasksTotal', taskMetrics, 0)}
						</span>
					</span>
				) : null}
			</div>
		</div>
	);
};

export default DeliverableListItem;
